import React, { useEffect, useState } from "react";
import AdminLayout from "../../Layout/index.jsx";
import jsFileDownload from "js-file-download";
import { pdf } from "@react-pdf/renderer";
import { useApolloClient } from "@apollo/client";
import {
  GetStudentsByCampusDocument,
  useGetStudentsByCampusQuery,
  useSendInviteEmailToStudentMutation,
  useGetCampusQuery,
  useGetClustersByCampusQuery,
} from "../../../../graphql/generated.tsx";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../../../utils/firebase";
import {
  IoMdAdd,
  IoMdArrowRoundBack,
  IoMdSend,
  IoMdTrash,
} from "react-icons/io";
import { MdCheckCircle, MdPictureAsPdf, MdTableChart } from "react-icons/md";
import { FaDownload } from "react-icons/fa6";
import { Button, Table, Spinner } from "flowbite-react";
import { toast } from "../../../../toast";
import { Link } from "react-router-dom";
import Pagination from "../../../../components/Pagination/index.jsx";
import { gql, useMutation } from "@apollo/client";
import swal from "sweetalert";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const superAdminEmails = [
  "training@thebettertomorrow.in",
  "bettertomorrowoffi@gmail.com",
  "beta-admin@bt.in",
];

const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      statusOfApproval
    }
  }
`;

function getRequestedBy() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const name = localStorage.getItem("userdisplayName") || localStorage.getItem("username");
  return name || user?.displayName || user?.userdisplayName || user?.email || "Unknown User";
}

function formatClusterSummary(clusterIds, clusterNameMap) {
  if (!clusterIds?.length) return "—";
  return clusterIds.map((cid) => clusterNameMap[cid] || cid).join(", ");
}

function displayCell(value) {
  if (value === null || value === undefined) return "—";
  const s = String(value).trim();
  return s.length ? s : "—";
}

function sanitizeFilePart(value) {
  return (
    String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "-")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "Student_Details"
  );
}

function formatStudentNamesLabel(records, max = 3) {
  const names = records.map((s) => s.name || s.email || "Student");
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max} more`;
}

const WHOLE_CAMPUS_VALUE = "__whole_campus__";

const studentDetailsPdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 9,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: "#6b7280",
    marginBottom: 12,
  },
  table: {
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderStyle: "solid",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
    borderBottomStyle: "solid",
  },
  headerRow: {
    backgroundColor: "#5c7a94",
  },
  cell: {
    paddingVertical: 5,
    paddingHorizontal: 4,
    borderRightWidth: 1,
    borderRightColor: "#cbd5e1",
    borderRightStyle: "solid",
    textAlign: "center",
  },
  headerCell: {
    color: "#ffffff",
    fontWeight: 700,
  },
  bodyText: {
    color: "#111827",
  },
});

function escapeCsvValue(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function getJoinedClusterNames(student, clusterNameMap) {
  return student?.clusterIds?.length
    ? student.clusterIds.map((cid) => clusterNameMap[cid] || cid).join(", ")
    : "—";
}

const StudentDetailsPdfDocument = ({ students, clusterNameMap, campusName, batchName }) => (
  <Document>
    <Page size="A4" orientation="landscape" style={studentDetailsPdfStyles.page}>
      <Text style={studentDetailsPdfStyles.title}>Student Details</Text>
      <Text style={studentDetailsPdfStyles.subtitle}>
        College: {displayCell(campusName)} | Batch: {displayCell(batchName)} | Total students: {students.length}
      </Text>

      <View style={studentDetailsPdfStyles.table}>
        <View style={[studentDetailsPdfStyles.row, studentDetailsPdfStyles.headerRow]}>
          {[
            ["S.No", "5%"],
            ["Name", "11%"],
            ["Roll No", "8%"],
            ["Email", "14%"],
            ["Department", "9%"],
            ["Phone", "9%"],
            ["LeetCode", "10%"],
            ["HackerRank", "10%"],
            ["DOB", "7%"],
            ["Place", "7%"],
            ["Clusters", "10%"],
          ].map(([label, width]) => (
            <Text key={label} style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.headerCell, { width }]}>
              {label}
            </Text>
          ))}
        </View>
        {students.map((student, index) => (
          <View key={student.id || student.email || index} style={studentDetailsPdfStyles.row}>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "5%" }]}>{index + 1}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "11%" }]}>{displayCell(student.name)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "8%" }]}>{displayCell(student.rollNo)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "14%" }]}>{displayCell(student.email)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "9%" }]}>{displayCell(student.department)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "9%" }]}>{displayCell(student.phno)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "10%" }]}>{displayCell(student.leetCodeUname)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "10%" }]}>{displayCell(student.hackerRankUname)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "7%" }]}>{displayCell(student.dob)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "7%" }]}>{displayCell(student.placeOfResidence)}</Text>
            <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.bodyText, { width: "10%", borderRightWidth: 0 }]}>
              {getJoinedClusterNames(student, clusterNameMap)}
            </Text>
          </View>
        ))}
        <View style={studentDetailsPdfStyles.row}>
          <Text style={[studentDetailsPdfStyles.cell, studentDetailsPdfStyles.headerCell, { width: "100%", borderRightWidth: 0 }]}>Purchased courses are exported in the CSV file.</Text>
        </View>
      </View>
    </Page>
  </Document>
);

/** Same SweetAlert layout as Manage Cluster → ClusterListing delete. */
function openStudentDeletionChoiceSwal(titleLabel) {
  return new Promise((resolve) => {
    const styleId = "swal-delete-cluster-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
          .swal-delete-cluster .swal-icon { display: none !important; }
          .swal-delete-cluster .swal-title { display: none !important; }
          .swal-delete-cluster .swal-text { display: none !important; }
          .swal-delete-cluster .swal-footer { display: none; }
          .swal-delete-cluster .swal-content { width: 100%; padding: 0 4px; margin-top: 0 !important; }
          .swal-delete-cluster { padding: 32px 28px 26px; width: 820px !important; min-height: 420px !important; position: relative !important; border: 2px solid #2f5c7c !important; }
          .swal-del-btn { background: #e5e7eb !important; color: #0E3D5D !important; transition: background 0.18s, color 0.18s !important; }
          .swal-del-btn:hover { background: #d1d5db !important; color: #0E3D5D !important; }
          .swal-del-close-btn:hover { background: #f3f4f6 !important; }
        `;
      document.head.appendChild(style);
    }

    const safeName = String(titleLabel || "Student").replace(/"/g, "&quot;");
    const content = document.createElement("div");
    content.style.cssText = "display:flex;flex-direction:column;gap:10px;width:100%;";
    content.innerHTML = `
        <div style="position:relative;">
          <button id="swal-stu-del-close" class="swal-del-close-btn" style="
            position:absolute;top:-10px;right:-10px;
            width:30px;height:30px;border-radius:50%;border:none;cursor:pointer;
            background:#f3f4f6;color:#6b7280;font-size:18px;line-height:1;
            display:flex;align-items:center;justify-content:center;font-weight:400;
          ">&#x2715;</button>
        </div>
        <div style="text-align:center;margin-bottom:14px;">
          <div style="
            display:inline-flex;align-items:center;justify-content:center;
            width:56px;height:56px;border-radius:50%;
            background:#e8f0f7;color:#0E3D5D;font-size:28px;font-weight:700;
            margin-bottom:12px;
          ">!</div>
          <div style="font-size:18px;font-weight:700;color:#111827;margin-bottom:4px;">Delete "${safeName}"</div>
          <div style="font-size:13px;color:#6b7280;">Choose a deletion option:</div>
        </div>
        <div style="display:flex;flex-direction:row;gap:16px;width:100%;align-items:flex-start;">
          <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:10px;">
            <button id="swal-stu-del-only" class="swal-del-btn" style="
              width:100%;padding:16px 14px;border-radius:8px;border:none;cursor:pointer;
              background:#e5e7eb;color:#0E3D5D;font-size:14px;font-weight:700;letter-spacing:0.01em;
            ">Clear Submissions</button>
            <ul style="font-size:12.5px;color:#374151;text-align:left;line-height:1.8;padding-left:18px;margin:0;list-style:disc;">
              <li>Clears this student's assignment submissions</li>
              <li>Clusters, purchases, and profile stay <strong>intact</strong></li>
            </ul>
          </div>
          <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:10px;">
            <button id="swal-stu-del-all" class="swal-del-btn" style="
              width:100%;padding:16px 14px;border-radius:8px;border:none;cursor:pointer;
              background:#e5e7eb;color:#0E3D5D;font-size:14px;font-weight:700;letter-spacing:0.01em;
            ">Clear Student Profile</button>
            <ul style="font-size:12.5px;color:#374151;text-align:left;line-height:1.8;padding-left:18px;margin:0;list-style:disc;">
              <li>Requests full removal of the student from this campus</li>
              <li>Removes enrolled courses and account per policy</li>
              <li>This action <strong>cannot be undone</strong></li>
            </ul>
          </div>
        </div>
      `;

    swal({
      buttons: false,
      content,
      className: "swal-delete-cluster",
    });

    content.querySelector("#swal-stu-del-only").onclick = () => {
      swal.close();
      resolve("submissionsOnly");
    };
    content.querySelector("#swal-stu-del-all").onclick = () => {
      swal.close();
      resolve("fullProfile");
    };
    content.querySelector("#swal-stu-del-close").onclick = () => {
      swal.close();
      resolve(null);
    };
  });
}

const StudentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const apolloClient = useApolloClient();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && !superAdminEmails.includes(user.email)) {
        navigate("/admin", { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedClusterId, setSelectedClusterId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 20;

  const { data: campusDetails } = useGetCampusQuery({ variables: { id } });
  const campusName = campusDetails?.campus?.name;

  const { data: clustersData, loading: clustersLoading } =
    useGetClustersByCampusQuery({
      variables: { campusName: campusName || "", page: 1, limit: 200 },
      skip: !campusName,
    });

  const campusClusters = clustersData?.getClustersByCampus ?? [];
  const sortedClusters = [...campusClusters].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const clusterNameMap = Object.fromEntries(
    campusClusters.map((c) => [c.id, c.name])
  );

  const isWholeCampus = selectedClusterId === WHOLE_CAMPUS_VALUE;
  const hasClusterSelected =
    Boolean(selectedClusterId) && !isWholeCampus;
  const hasWholeCampusSelected = isWholeCampus;
  const hasSearchTerm = Boolean(debouncedSearch.trim());
  const shouldFetchStudents =
    hasClusterSelected || hasWholeCampusSelected || hasSearchTerm;

  const studentQueryVariables = {
    campusId: id,
    searchTerm: debouncedSearch,
    page: currentPage,
    limit,
    ...(hasClusterSelected ? { clusterid: selectedClusterId } : {}),
  };

  const [selectedStudents, setSelectedStudents] = useState([]);

  const [inviteStudent] = useSendInviteEmailToStudentMutation();
  const [requestDeletion] = useMutation(REQUEST_DELETION);

  const [hoveredRow, setHoveredRow] = useState(null);
  const [isInviting, setIsInviting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 700);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedStudents([]);
  }, [selectedClusterId]);

  const { loading, data, refetch } = useGetStudentsByCampusQuery({
    variables: studentQueryVariables,
    skip: !id || !shouldFetchStudents,
    fetchPolicy: "network-only",
  });

  const students = data?.getStudentsByCampus?.students || [];
  const total = data?.getStudentsByCampus?.total || 0;
  const totalPages = Math.ceil(total / limit) || 1;
  const visibleStudents = students;
  const selectedBatchName = isWholeCampus
    ? "Whole Campus"
    : sortedClusters.find((cluster) => cluster.id === selectedClusterId)?.name || displayCell(selectedClusterId);

  const fetchAllStudentsForExport = async () => {
    if (!id || !shouldFetchStudents) return [];

    const baseVariables = {
      campusId: id,
      searchTerm: debouncedSearch,
      ...(hasClusterSelected ? { clusterid: selectedClusterId } : {}),
    };

    const pageSize = 100;
    const firstResult = await apolloClient.query({
      query: GetStudentsByCampusDocument,
      variables: {
        ...baseVariables,
        page: 1,
        limit: pageSize,
      },
      fetchPolicy: "network-only",
    });

    const firstPageStudents = firstResult?.data?.getStudentsByCampus?.students || [];
    const totalStudents = firstResult?.data?.getStudentsByCampus?.total || 0;
    if (totalStudents <= firstPageStudents.length) {
      return firstPageStudents;
    }

    const totalPagesForExport = Math.ceil(totalStudents / pageSize);
    const pages = [firstPageStudents];

    for (let page = 2; page <= totalPagesForExport; page += 1) {
      const result = await apolloClient.query({
        query: GetStudentsByCampusDocument,
        variables: {
          ...baseVariables,
          page,
          limit: pageSize,
        },
        fetchPolicy: "network-only",
      });
      pages.push(result?.data?.getStudentsByCampus?.students || []);
    }

    return pages.flat();
  };

  const handleSelectStudent = (email) => {
    setSelectedStudents((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    );
  };

  /** Manage Cluster–style: pick submissions vs profile → reason → requestDeletion (same pattern as ClusterListing). */
  const handleDeleteStudentFlow = async (studentId) => {
    const student = students.find((s) => s.id === studentId);
    const campusNameVal = campusDetails?.campus?.name;
    if (!campusNameVal) {
      swal("Error", "Campus not loaded yet. Try again.", "error");
      return;
    }

    const deleteType = await openStudentDeletionChoiceSwal(student?.name || student?.email || "Student");
    if (!deleteType) return;

    const reason = await swal({
      text: `Enter reason for requesting deletion for student "${student?.name || student?.email}":`,
      content: "input",
      buttons: {
        cancel: "Cancel",
        confirm: { text: "Submit", closeModal: false },
      },
    });

    if (!reason) {
      swal.close();
      return;
    }

    const requestedBy = getRequestedBy();
    const clusterSummary = formatClusterSummary(student?.clusterIds, clusterNameMap);

    try {
      setIsDeleting(true);

      if (deleteType === "submissionsOnly") {
        const submissionPayload = {
          operationName: "DeleteStudentSubmissions",
          variables: {
            id: studentId,
            campusName: campusNameVal,
          },
          query: `
            mutation DeleteStudentSubmissions($id: String!, $campusName: String!) {
              deleteStudentSubmissions(id: $id, campusName: $campusName)
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "STUDENT_SUBMISSION",
            payload: submissionPayload,
            reasonForDeletion: reason,
            requestedBy,
            title: `Clear submissions — ${student?.name || student?.email || studentId} (${clusterSummary})`,
          },
        });
      } else {
        const profilePayload = {
          operationName: "DeleteStudent",
          variables: {
            id: studentId,
            campusName: campusNameVal,
          },
          query: `
            mutation DeleteStudent($id: String!, $campusName: String!) {
              deleteStudent(id: $id, campusName: $campusName)
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "STUDENT",
            payload: profilePayload,
            reasonForDeletion: reason,
            requestedBy,
            title: `${student?.name || student?.email || "Student"} (${clusterSummary})`,
          },
        });
      }

      await swal({
        title: "Success",
        text: "Deletion request sent successfully!",
        icon: "success",
        buttons: { confirm: true },
        timer: 1800,
      });

      refetch(studentQueryVariables);
    } catch (err) {
      console.error(err);
      swal({
        title: "Error",
        text: "Failed to send deletion request",
        icon: "error",
        buttons: { cancel: "Close" },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDeleteFlow = async () => {
    const campusNameVal = campusDetails?.campus?.name;
    if (!campusNameVal) {
      swal("Error", "Campus not loaded yet. Try again.", "error");
      return;
    }

    const selectedRecords = students.filter((s) => selectedStudents.includes(s.email));
    if (!selectedRecords.length) {
      swal("Error", "No students selected.", "error");
      return;
    }

    const namesLabel = formatStudentNamesLabel(selectedRecords);
    const modalTitle =
      selectedRecords.length === 1
        ? selectedRecords[0].name || selectedRecords[0].email || "Student"
        : `${selectedRecords.length} selected students`;

    const deleteType = await openStudentDeletionChoiceSwal(modalTitle);
    if (!deleteType) return;

    const reason = await swal({
      text: `Enter reason for deletion request (${namesLabel}):`,
      content: "input",
      buttons: {
        cancel: "Cancel",
        confirm: { text: "Submit", closeModal: false },
      },
    });

    if (!reason) {
      swal.close();
      return;
    }

    const requestedBy = getRequestedBy();
    const allClusterNames = [
      ...new Set(
        selectedRecords.flatMap((s) =>
          (s.clusterIds || []).map((cid) => clusterNameMap[cid] || cid)
        )
      ),
    ];
    const clusterSummary = allClusterNames.length ? allClusterNames.join(", ") : "—";
    const studentEmails = selectedRecords.map((s) => s.email).filter(Boolean);

    try {
      setIsDeleting(true);

      if (deleteType === "submissionsOnly") {
        await Promise.all(
          selectedRecords.map((student) => {
            const clusterLabel = formatClusterSummary(student.clusterIds, clusterNameMap);
            const submissionPayload = {
              operationName: "DeleteStudentSubmissions",
              variables: {
                id: student.id,
                campusName: campusNameVal,
              },
              query: `
                mutation DeleteStudentSubmissions($id: String!, $campusName: String!) {
                  deleteStudentSubmissions(id: $id, campusName: $campusName)
                }
              `,
            };

            return requestDeletion({
              variables: {
                typeOfDeletion: "STUDENT_SUBMISSION",
                payload: submissionPayload,
                reasonForDeletion: reason,
                requestedBy,
                title: `Clear submissions — ${student.name || student.email} (${clusterLabel})`,
              },
            });
          })
        );
      } else {
        const profilePayload = {
          operationName: "DeleteBulkStudents",
          variables: {
            studentIds: studentEmails,
            campusName: campusNameVal,
          },
          query: `
            mutation DeleteBulkStudents($studentIds: [String!]!, $campusName: String!) {
              deleteBulkStudents(studentIds: $studentIds, campusName: $campusName) {
                successCount
                failedCount
                deletedEmails
                failedStudents {
                  email
                  studentId
                  reason
                }
                message
              }
            }
          `,
        };

        await requestDeletion({
          variables: {
            typeOfDeletion: "BULK_STUDENT",
            payload: profilePayload,
            reasonForDeletion: reason,
            requestedBy,
            title: `Bulk profile deletion — ${namesLabel} (clusters: ${clusterSummary})`,
          },
        });
      }

      await swal({
        title: "Success",
        text: "Deletion request sent successfully!",
        icon: "success",
        buttons: { confirm: true },
        timer: 1800,
      });

      setSelectedStudents([]);
      refetch(studentQueryVariables);
    } catch (err) {
      console.error(err);
      swal({
        title: "Error",
        text: "Failed to send deletion request",
        icon: "error",
        buttons: { cancel: "Close" },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const downloadCSV = async () => {
    const exportStudents = await fetchAllStudentsForExport();
    if (!exportStudents.length) {
      toast.error("No students available to export.");
      return;
    }

    const headers = [
      "S.No",
      "Name",
      "Roll No",
      "Email",
      "Department",
      "Phone",
      "LeetCode",
      "HackerRank",
      "DOB",
      "Place",
      "Clusters",
      "Purchased Courses",
    ];

    const rows = exportStudents.map((student, index) => [
      index + 1,
      displayCell(student.name),
      displayCell(student.rollNo),
      displayCell(student.email),
      displayCell(student.department),
      displayCell(student.phno),
      displayCell(student.leetCodeUname),
      displayCell(student.hackerRankUname),
      displayCell(student.dob),
      displayCell(student.placeOfResidence),
      getJoinedClusterNames(student, clusterNameMap),
      student.purchasedCourses?.length
        ? student.purchasedCourses.map((course) => course?.course_id).filter(Boolean).join(" | ")
        : "—",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    jsFileDownload(
      csvContent,
      `${sanitizeFilePart(campusName)}_${sanitizeFilePart(selectedBatchName)}_students.csv`
    );
  };

  const handleDownloadPDF = async () => {
    if (pdfLoading) return;

    setPdfLoading(true);
    try {
      const exportStudents = await fetchAllStudentsForExport();
      if (!exportStudents.length) {
        toast.error("No students available to export.");
        return;
      }

      const blob = await pdf(
        <StudentDetailsPdfDocument
          students={exportStudents}
          clusterNameMap={clusterNameMap}
          campusName={campusName}
          batchName={selectedBatchName}
        />
      ).toBlob();

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `${sanitizeFilePart(campusName)}_${sanitizeFilePart(selectedBatchName)}_students.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowDownloadDropdown(false);
    } catch (error) {
      console.error("StudentDetails PDF:", error);
      toast.error(error?.message || "Failed to generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const SkeletonRows = () => (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border border-gray-200 rounded-lg bg-white"
        >
          <div className="w-4 h-4 bg-gray-300 rounded shrink-0" />
          <div className="h-4 flex-1 min-w-[12rem] bg-gray-300 rounded" />
          <div className="h-4 w-32 bg-gray-300 rounded shrink-0 hidden sm:block" />
          <div className="h-4 w-24 bg-gray-300 rounded shrink-0 hidden md:block" />
        </div>
      ))}
    </div>
  );

  const thClass =
    "text-white text-center whitespace-nowrap text-xs font-semibold uppercase tracking-wide px-4 py-4 min-w-[5.5rem]";
  const tdClass =
    "text-sm text-gray-800 text-center align-middle px-4 py-5 whitespace-normal break-words leading-relaxed";

  return (
    <AdminLayout>
      <div className="p-10 bg-white min-h-screen" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <button onClick={() => window.history.back()} className="text-gray-700 hover:text-gray-900 transition-colors">
              <IoMdArrowRoundBack className="w-6 h-6" />
            </button>
            <div className="ml-2 text-2xl font-bold text-gray-800 tracking-tight">Student Details</div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleBulkDeleteFlow}
              disabled={!selectedStudents.length || isDeleting}
              className="bg-red-600"
            >
              <IoMdTrash className="mr-1" /> Delete Selected
            </Button>

            <Link to={`create-students`}>
              <Button className="bg-primary">
                <IoMdAdd className="mr-1" /> Create Student
              </Button>
            </Link>
          </div>
        </div>

        <div className="mb-6 rounded-lg border border-red-400 bg-red-100 px-4 py-3 text-sm text-red-800 flex items-start gap-2 shadow-sm">
          <span className="font-semibold">Note:</span>
          <span>
            Row delete and <strong>Delete Selected</strong> use the same two-step flow as Manage
            Cluster: <strong>Clear Submissions</strong> or <strong>Clear Student Profile</strong>.
            Requests include student name(s) and cluster(s). Admin approval required.
          </span>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div className="flex flex-col gap-1 min-w-[220px]">
            <label htmlFor="cluster-filter" className="text-sm font-medium text-gray-700">
              Cluster
            </label>
            <select
              id="cluster-filter"
              value={selectedClusterId}
              onChange={(e) => setSelectedClusterId(e.target.value)}
              disabled={clustersLoading || !campusName}
              className="px-3 py-2 border border-gray-400 rounded-lg text-gray-800 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 min-w-[220px] disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select cluster</option>
              <option value={WHOLE_CAMPUS_VALUE}>Whole Campus</option>
              {sortedClusters.map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Search by name, email or roll no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 border border-gray-400 rounded-lg w-80 text-gray-800 placeholder-gray-500 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            <div className="relative">
              <button
                onClick={() => setShowDownloadDropdown((prev) => !prev)}
                className="inline-flex items-center justify-center w-10 h-10 text-gray-600 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900"
                title="Download"
                aria-label="Download"
              >
                <FaDownload size={18} />
              </button>

              {showDownloadDropdown && (
                <div className="absolute right-0 z-20 p-2 mt-2 bg-white border border-gray-200 shadow-lg top-full w-44 rounded-xl">
                  <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                    Choose format
                  </p>

                  <button
                    onClick={() => {
                      void downloadCSV();
                      setShowDownloadDropdown(false);
                    }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <span className="rounded-lg bg-gray-100 p-1.5 text-gray-600">
                      <MdTableChart size={17} />
                    </span>
                    <span className="text-sm font-medium">CSV</span>
                  </button>

                  <button
                    onClick={() => void handleDownloadPDF()}
                    disabled={pdfLoading}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                      pdfLoading ? "text-gray-400" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`rounded-lg p-1.5 ${
                        pdfLoading ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <MdPictureAsPdf size={17} />
                    </span>
                    <span className="text-sm font-medium">
                      {pdfLoading ? "Loading..." : "PDF"}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {clustersLoading ? (
          <SkeletonRows />
        ) : !shouldFetchStudents ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <svg
              className="w-12 h-12 mb-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <p className="text-lg font-semibold text-gray-700">
              Search, pick Whole Campus, or select a cluster
            </p>
            <p className="text-sm text-gray-500">
              Type a name, email or roll no — or choose Whole Campus / a cluster to list students
            </p>
          </div>
        ) : loading ? (
          <SkeletonRows />
        ) : students.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <p className="text-lg font-semibold text-gray-700">No students found</p>
            <p className="text-sm text-gray-500">
              Try Whole Campus, another cluster, or a different search term
            </p>
          </div>
        ) : (
        <>
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <Table className="min-w-max w-full [&_tbody_tr]:border-b [&_tbody_tr]:border-gray-100">
          <Table.Head style={{ backgroundColor: "#5c7a94" }}>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[3.5rem]`}>
              Select
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[9rem]`}>
              Name
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[14rem]`}>
              Email
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[6rem]`}>
              Roll no
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[8rem]`}>
              Department
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[7rem]`}>
              Phone
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[11rem]`}>
              LeetCode
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[11rem]`}>
              HackerRank
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[7rem]`}>
              DOB
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[8rem]`}>
              Place
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[10rem]`}>
              Clusters
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[12rem]`}>
              Purchased courses
            </Table.HeadCell>
            <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className={`${thClass} min-w-[5rem]`}>
              Actions
            </Table.HeadCell>
          </Table.Head>

          <Table.Body>
            {students.map((student) => (
              <Table.Row
                key={student.id}
                onMouseEnter={() => setHoveredRow(student.id)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <Table.Cell className={`${tdClass} min-w-[3.5rem]`}>
                  <div className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.email)}
                      onChange={() => handleSelectStudent(student.email)}
                    />
                  </div>
                </Table.Cell>

                <Table.Cell className={`${tdClass} min-w-[9rem]`}>
                  {displayCell(student.name)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[14rem]`}>
                  {displayCell(student.email)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[6rem]`}>
                  {displayCell(student.rollNo)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[8rem]`}>
                  {displayCell(student.department)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[7rem]`}>
                  {displayCell(student.phno)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[11rem]`}>
                  {student.leetCodeUname?.trim() ? (
                    <a
                      href={`https://leetcode.com/u/${encodeURIComponent(student.leetCodeUname.trim())}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {student.leetCodeUname.trim()}
                    </a>
                  ) : (
                    "—"
                  )}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[11rem]`}>
                  {student.hackerRankUname?.trim() ? (
                    <a
                      href={`https://www.hackerrank.com/profile/${encodeURIComponent(student.hackerRankUname.trim())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline break-all"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {student.hackerRankUname.trim()}
                    </a>
                  ) : (
                    "—"
                  )}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[7rem]`}>
                  {displayCell(student.dob)}
                </Table.Cell>
                <Table.Cell className={`${tdClass} min-w-[8rem]`}>
                  {displayCell(student.placeOfResidence)}
                </Table.Cell>

                <Table.Cell className={`${tdClass} min-w-[10rem]`}>
                  {student.clusterIds?.length
                    ? student.clusterIds
                        .map((cid) => clusterNameMap[cid] || cid)
                        .join(", ")
                    : "—"}
                </Table.Cell>

                <Table.Cell className={`${tdClass} min-w-[12rem]`}>
                  {student.purchasedCourses?.length ? (
                    <div className="flex flex-wrap justify-center gap-1">
                      {student.purchasedCourses.map((pc, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 text-xs font-semibold bg-green-200 text-green-800 rounded-full break-all"
                        >
                          {pc.course_id}
                        </span>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </Table.Cell>

                <Table.Cell className={`${tdClass} min-w-[5rem]`}>
                  <div className="flex justify-center gap-2 min-h-[1.25rem]">
                    {hoveredRow === student.id && (
                      <IoMdTrash
                        className="w-5 h-5 text-red-500 cursor-pointer"
                        title="Request deletion"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isDeleting) handleDeleteStudentFlow(student.id);
                        }}
                      />
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        </div>

        {total > limit && (
          <p className="mt-4 text-center text-sm text-gray-600">
            Showing {(currentPage - 1) * limit + 1}–{Math.min(currentPage * limit, total)} of{" "}
            {total} students
          </p>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
        </>
        )}
      </div>

    </AdminLayout>
  );
};

export default StudentDetails;
